/**
 * @license Copyright (c) 2014-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

import {ClassicEditor} from '@ckeditor/ckeditor5-editor-classic';
import {Autoformat} from '@ckeditor/ckeditor5-autoformat';
import {Bold, Italic, Underline} from '@ckeditor/ckeditor5-basic-styles';
import {BlockQuote} from '@ckeditor/ckeditor5-block-quote';
import type {EditorConfig} from '@ckeditor/ckeditor5-core';
import {Essentials} from '@ckeditor/ckeditor5-essentials';
import {FontColor, FontSize, FontFamily} from '@ckeditor/ckeditor5-font';
import {Heading} from '@ckeditor/ckeditor5-heading';
import {FullPage} from '@ckeditor/ckeditor5-html-support';
import {
    AutoImage,
    Image,
    ImageCaption,
    ImageInsert,
    ImageToolbar,
    ImageInsertViaUrl,
    ImageUpload,
} from '@ckeditor/ckeditor5-image';
import {Indent} from '@ckeditor/ckeditor5-indent';
import {AutoLink, Link} from '@ckeditor/ckeditor5-link';
import {List} from '@ckeditor/ckeditor5-list';
import {MediaEmbed} from '@ckeditor/ckeditor5-media-embed';

import {TextTransformation} from '@ckeditor/ckeditor5-typing';
import {Undo} from '@ckeditor/ckeditor5-undo';
import SpoilerPlugin from"../src/plugins/spoiler-plugin";
// import {SourceEditing} from '@ckeditor/ckeditor5-source-editing';
import {Alignment} from '@ckeditor/ckeditor5-alignment';

class Editor extends ClassicEditor {
    public static override builtinPlugins = [
        AutoImage,
        AutoLink,
        Autoformat,
        BlockQuote,
        Bold,
        Essentials,
        FontColor,
        FontSize,
        FontFamily,
        FullPage,
        Heading,
        Image,
        ImageCaption,
        ImageInsertViaUrl,
        ImageInsert,
        ImageToolbar,
        ImageUpload,
        Indent,
        Italic,
        Link,
        List,
        MediaEmbed,
        TextTransformation,
        Undo,
        SpoilerPlugin,
        Alignment,
        Underline
    ];

    public static override defaultConfig: EditorConfig = {

        toolbar: {
            items: [
                'bold',
                'italic',
                'underline',
                '|',
                'fontColor',
                'fontSize',
                'fontFamily',
                '|',
                'link',
                'alignment',
                'bulletedList',
                'numberedList',
                '|',
                'outdent',
                'indent',
                '|',
                'insertImageViaUrl',
                'blockQuote',
                'mediaEmbed',
                'spoiler',
                'undo',
                'redo',
                '|',

            ]
        },
        language: 'uk',
        image: {
            toolbar: [
                'imageTextAlternative',
                'toggleImageCaption'
            ]
        },
        alignment: {
            options: ['left', 'right', 'center', "justify"]
        },
        fontSize: {
            options: [
             10, 12, 14, 16, 18, 20, 22, 24, 26
            ],
            supportAllValues: true // ��������� ��� ��������, ������� custom
        },
        // ��� ������������� ����� <font size="X">
        htmlSupport: {
            allow: [
                {
                    name: 'font',
                    attributes: {
                        size: true
                    }
                },
                {
                    name: 'p', // ��������� ������������� ����� ��� ���� <p>
                    attributes: {
                        style: true // ��������� ������� style
                    }
                }
            ]
        },

        fontColor: {
            colors: [
                {
                    color: '#E64C4C',
                    label: 'Red Background'
                },
                {
                    color: '#4CE6E6',
                    label: 'Cyan Background'
                },
                {
                    color: '#4CE64C',
                    label: 'Green Background'
                },
                {
                    color: '#E6E64C',
                    label: 'Yellow Background'
                },
                {
                    color: '#4C4CE6',
                    label: 'Blue Background'
                },
                {
                    color: '#000000',
                    label: 'Black Background'
                },
                {
                    color: '#4A4A4A',
                    label: 'Dark Grey Background'
                },
                {
                    color: '#888888',
                    label: 'Grey Background'
                },
                {
                    color: '#CCCCCC',
                    label: 'Light Grey Background'
                },
                {
                    color: '#FFFFFF',
                    label: 'White Background',
                    hasBorder: true
                }
            ],
            colorPicker: {
                // Use 'hex' format for output instead of 'hsl'.
                format: 'hex'
            }
        },

        fontBackgroundColor: {
            colors: [
                {
                    color: '#E64C4C',
                    label: 'Red Background'
                },
                {
                    color: '#4CE6E6',
                    label: 'Cyan Background'
                },
                {
                    color: '#4CE64C',
                    label: 'Green Background'
                },
                {
                    color: '#E6E64C',
                    label: 'Yellow Background'
                },
                {
                    color: '#4C4CE6',
                    label: 'Blue Background'
                },
                {
                    color: '#000000',
                    label: 'Black Background'
                },
                {
                    color: '#4A4A4A',
                    label: 'Dark Grey Background'
                },
                {
                    color: '#888888',
                    label: 'Grey Background'
                },
                {
                    color: '#CCCCCC',
                    label: 'Light Grey Background'
                },
                {
                    color: '#FFFFFF',
                    label: 'White Background',
                    hasBorder: true
                }
            ],
            colorPicker: {
                // Use 'hex' format for output instead of 'hsl'.
                format: 'hex'
            },
        },
        mediaEmbed: {
            previewsInData: true,
            extraProviders: [
                {
                    name: 'youtube-shorts',
                    url: [
                        /^https?:\/\/(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]+)$/,
                        /^(?:m\.)?youtube\.com\/shorts\/(.+)/,
                    ],
                    html: match => {
                        const videoId = match[1];
                        return (
                            `<div style="position: relative; padding-bottom: 100%; height: 0; padding-bottom: 56.2493%;">` +
                            `<iframe src="https://www.youtube.com/embed/${videoId}" ` +
                            `style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;" ` +
                            `frameborder="0" allow="autoplay; encrypted-media" allowfullscreen>` +
                            `</iframe>` +
                            `</div>`
                        );
                    }
                },
                {
                    name: 'tiktok',
                    url: /^https:\/\/www\.tiktok\.com\/(@[\w.-]+\/video\/\d+)/,
                    html: match =>
                        `<div style="position: relative; padding-bottom: 100%; height: 0; padding-bottom: 56.2493%;">` +
                        `<iframe src="https://www.tiktok.com/player/v1/${match[1].split('/').pop()}?music_info=1&description=1" 
                    allowfullscreen 
                    title="TikTok Video"
                    style="border:0;height:100%;left:0;position:absolute;width:100%"
                    >
                </iframe>` +
                        `</div>`
                }
            ]
        },

    };
}export default Editor;
